import {
  AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  setupSlice,
} from '@owl-frontend/redux';
import { listThreads as listThreadsApi } from '../../api/agentChat/threads/client';
import type { Thread } from '../../api/agentChat/threads/interface';

const listThreads = (dossierId: string): Promise<Thread[]> =>
  listThreadsApi(dossierId);

export interface AgentChatThreadsState {
  threads: {
    [dossier_id: string]: Thread[];
  };
  actions: {
    listThreads: AsyncActionState<Thread[]>;
  };
}

const initialState: AgentChatThreadsState = {
  threads: {},
  actions: {
    listThreads: { status: 'uninitialized' },
  },
};

function upsert(list: Thread[], thread: Thread): Thread[] {
  const idx = list.findIndex((t) => t.thread_id === thread.thread_id);
  if (idx === -1) {
    return [...list, thread];
  }
  const next = list.slice();
  next[idx] = thread;
  return next;
}

// Plain reducers below (threadUpserted, threadRemoved) generate matching action
// creators on slice.actions. Mutations stay as direct API calls in the
// component (so the caller can await + immediately activate the resulting
// thread); after each call, dispatch the corresponding sync action to keep
// the slice cache aligned.
const slice = setupSlice('agentChatThreads', initialState)
  .addAsyncSagas({
    listThreads: invoke(listThreads),
  })
  .addReducers({
    'listThreads/fulfilled': (state, action) => {
      state.threads[action.meta.arg] = action.payload;
    },
  })
  .addReducers({
    threadUpserted: (
      state,
      action: { payload: { dossierId: string; thread: Thread } }
    ) => {
      const { dossierId, thread } = action.payload;
      state.threads[dossierId] = upsert(state.threads[dossierId] ?? [], thread);
    },
    threadRemoved: (
      state,
      action: { payload: { dossierId: string; threadId: string } }
    ) => {
      const { dossierId, threadId } = action.payload;
      state.threads[dossierId] = (state.threads[dossierId] ?? []).filter(
        (t) => t.thread_id !== threadId
      );
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
